import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import OrderDetailModal from "../../common/orderDetailModal/OrderDetailModal";
import UserAvatar from "../../common/avatar/UserAvatar";
import configuration from "../../config/Config";
import { useParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Paginations from "../../common/pagination/Paginations";
import { allOrderListApi, OrderDetailsApi } from "../../api";
import { useSelector } from "react-redux";
import { AdminTable, ActionCell } from "../../common/adminTable";
import { formatMoney } from "../../utils/configUtils";
import StatusChip from "../../common/statusChip/StatusChip";

const AllOrder = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const { id } = useParams();
  const [allOrderData, setAllOrderData] = useState([]);
  const [orderProducts, setOrderProducts] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [myTimout, setMyTimeOut] = useState();
  const [open, setOpen] = useState(false);

  const handleAllOrderList = async () => {
    try {
      setLoading(true);
      const response = await allOrderListApi({ token, currentPage, id, searchQuery });
      setAllOrderData(response?.orders || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  useEffect(() => {
    handleAllOrderList();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      handleAllOrderList();
    } else {
      clearTimeout(myTimout);
      const timeout = setTimeout(() => handleAllOrderList(), 500);
      setMyTimeOut(timeout);
    }
  }, [searchQuery]);

  const handleOrderDetail = async (orderId) => {
    try {
      const response = await OrderDetailsApi({ token, id: orderId });
      setOrderProducts(response || null);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleClickOpen = (row) => {
    handleOrderDetail(row?._id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const columns = [
    {
      field: "seller",
      headerName: "Seller Name",
      minWidth: 160,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <UserAvatar
            src={row?.seller?.picture ? `${configuration.cdnFront}/${row.seller.picture}` : undefined}
            name={row?.seller?.fullname}
            size={32}
            sx={{ flexShrink: 0 }}
          />
          <Typography sx={{ fontFamily: "Heebo", fontSize: "13px", color: "#362B24", whiteSpace: "nowrap" }}>
            {row?.seller?.fullname || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      minWidth: 140,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (row) =>
        row?.address ? [row.address.address || ""].filter(Boolean).join(", ") : "-",
    },
    {
      field: "totalAmount",
      headerName: "Total Price",
      width: 110,
      renderCell: (row) => formatMoney(row?.totalAmount),
    },
    {
      field: "quantity",
      headerName: "Quantity",
      width: 90,
      renderCell: (row) =>
        row?.items?.reduce((total, item) => total + (item?.quantity || 0), 0),
    },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      width: 130,
      renderCell: (row) => <StatusChip value={row?.paymentStatus} />,
    },
    {
      field: "status",
      headerName: "Order Status",
      width: 110,
      renderCell: (row) => <StatusChip value={row?.status} />,
    },
    {
      field: "__actions",
      headerName: "",
      width: 60,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleClickOpen(row),
            },
          ]}
        />
      ),
    },
  ];


  return (
    <div>
      <Card sx={{ padding: "20px", borderRadius: "25px", marginTop: "40px", marginBottom: "30px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          <Typography sx={{ fontSize: { xs: "16px", sm: "20px" }, fontWeight: "600", color: "#362B24" }}>
            Order List
          </Typography>
          <TextField
            placeholder="Search by order status"
            value={searchQuery}
            onChange={(e) => {
              if (e.target.value.trim() !== "" || e.target.value === "") {
                setSearchQuery(e.target.value);
              }
            }}
            size="small"
            sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                background: "#fff",
                fontFamily: "Heebo",
                "& fieldset": { borderColor: "#E7E7E7" },
                "&:hover fieldset": { borderColor: "#D7D7D7" },
                "&.Mui-focused fieldset": { borderColor: "#065F46" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#8B8B8B", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <AdminTable
          columns={columns}
          rows={allOrderData}
          loading={loading}
          rowKey="_id"
          emptyText="No orders found"
        />
        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
          disabled={loading}
        />
      </Card>

      <OrderDetailModal
        open={open}
        onClose={handleClose}
        order={orderProducts}
        items={orderProducts?.items}
      />
    </div>
  );
};

export default AllOrder;
